import React, { useCallback, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useMatch } from 'react-router-dom';
import { matchRoute } from '@/domain/router/utils';
import { getRuntimeConfig } from '@/lib/runtimeConfig';
import { usePopupState, bindTrigger, bindMenu } from 'material-ui-popup-state/hooks';
import { documentHotkeysIsLoadingSelector, documentHotkeysSelector } from '@/domain/documents';
import { helpdeskLinkSelector } from '@/domain/env';
import { toggleDocumentHotkeysModalAction, toggleWorkspaceHotkeysModalAction } from '@/domain/ui';
import { useTheme } from '@mui/material';
import * as ACL from '@/domain/restriction';
import useScript from '@/hooks/useScript';
import ROUTES_PATH from '@/router/routesPathConfig';

import Tooltip from '@/components/mui/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import { ListItemIcon } from '@/components/mui/Layouts/components/Drawer';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import useOpenChat from '@/components/ContactSupportMessage/useOpenChat';

import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LiveHelpIcon from '@mui/icons-material/LiveHelp';

const mapStateToProps = (state) => ({
  isLoadingHotkeys: documentHotkeysIsLoadingSelector(state),
  hotkeys: documentHotkeysSelector(state),
  isGranted: ACL.isGranted(state),
  helpdeskLink: helpdeskLinkSelector(state),
});

const HelpDropdown = () => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const { isGranted, hotkeys, isLoadingHotkeys, helpdeskLink } = useSelector(mapStateToProps);
  const isAllowedFreshdeskByACL = isGranted(ACL.ALLOW_FRESHDESK);
  const isDocumentPage = useMatch(ROUTES_PATH.DOCUMENT.absolute);
  const openChat = useOpenChat();

  const popupState = usePopupState({ variant: 'popover', popupId: 'HelpDropdown' });
  const { labelOptions } = useTheme();

  const freshdeskStatus = useScript(
    isAllowedFreshdeskByACL
      ? `https://widget.freshworks.com/widgets/${getRuntimeConfig().env.REACT_APP_FRESHDESK_WIDGET_ID}.js`
      : undefined,
  );

  const isWorkspacePage = matchRoute.isCompanyWorkspace(window.location.pathname);

  const availableItems = {
    contactSupport: isAllowedFreshdeskByACL || labelOptions.name === 'AlPrime',
    helpDesk: Boolean(helpdeskLink),
    hotKeys: (isDocumentPage && !isLoadingHotkeys && hotkeys.size > 0 && isGranted(ACL.IS_ACCOUNT)) || isWorkspacePage,
  };

  const isVisible = Object.values(availableItems).filter(Boolean).length > 0;

  const onClickHotkey = useCallback(() => {
    const action = isWorkspacePage ? toggleWorkspaceHotkeysModalAction : toggleDocumentHotkeysModalAction;
    dispatch(action());
    popupState.close();
  }, [popupState, dispatch, isWorkspacePage]);

  const onClickContactSupport = useCallback(() => {
    openChat();

    popupState.close();
  }, [openChat, popupState]);

  const tTooltip =
    freshdeskStatus === 'ready' || availableItems.contactSupport
      ? {
          id: 'header.menu.help',
          defaultMessage: 'Help',
        }
      : {
          id: 'header.menu.helpDisabled',
          defaultMessage: 'Experiencing help widget issues',
        };

  useLayoutEffect(() => {
    if (freshdeskStatus === 'ready') {
      console.log('DOKKA LOG: Freshdesk script loaded');

      // hide widget launcher iframe via css, because sometimes freshdesk widget dont want hide through
      // window.FreshworksWidget('hide', 'launcher');
      // I guess Freshdesk script is not yet initialized when hide API is invoked
      // 1 - always hide using styles/css - see useCreateTheme file
      // 2- try hide use widget API after 1second timeout
      const timer = setTimeout(() => {
        window.FreshworksWidget('hide', 'launcher');
      }, 1000);

      return () => clearTimeout(timer);
    }

    return () => {};
  }, [freshdeskStatus]);

  return (
    isVisible && (
      <>
        <Tooltip t={tTooltip}>
          <IconButton color="inherit" {...bindTrigger(popupState)}>
            <HelpOutlineOutlinedIcon color="primary" />
          </IconButton>
        </Tooltip>

        <Menu
          {...bindMenu(popupState)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {availableItems.helpDesk && (
            <MenuItem
              component={Link}
              href={helpdeskLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={popupState.close}
            >
              <ListItemIcon>
                <InfoOutlinedIcon />
              </ListItemIcon>
              <ListItemText>
                {formatMessage({
                  id: 'header.menu.info.helpCenter',
                  defaultMessage: 'Help Center',
                })}
              </ListItemText>
            </MenuItem>
          )}

          {availableItems.contactSupport && (
            <MenuItem disabled={isAllowedFreshdeskByACL && freshdeskStatus !== 'ready'} onClick={onClickContactSupport}>
              <ListItemIcon>
                <LiveHelpIcon />
              </ListItemIcon>
              <ListItemText>
                {formatMessage({
                  id: 'header.menu.info.support',
                  defaultMessage: 'Contact support',
                })}
              </ListItemText>
            </MenuItem>
          )}

          {availableItems.hotKeys && (
            <MenuItem onClick={onClickHotkey}>
              <ListItemIcon>
                <KeyboardIcon />
              </ListItemIcon>
              <ListItemText>
                {formatMessage({
                  id: 'header.menu.info.hotkeys',
                  defaultMessage: 'Keyboard shortcuts',
                })}
              </ListItemText>
            </MenuItem>
          )}
        </Menu>
      </>
    )
  );
};

export default HelpDropdown;
